import { useState, useEffect, useRef } from 'react';
import { subscribe, SEVERITY } from '../services/errorService';

const DEDUP_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
const AUTO_DISMISS_MS = 5000;

/**
 * useErrorToast — Subscribe to errorService and surface user-facing errors.
 *
 * Behaviour:
 * - Only shows USER_FACING and CRITICAL errors (ignores BACKGROUND)
 * - Deduplicates: the same error message will not re-appear within 5 minutes
 * - Auto-dismisses after 5 s
 *
 * Returns:
 *   toastError  — current ErrorEntry to display, or null
 *   dismissToast — call to clear immediately
 */
export function useErrorToast() {
  const [toastError, setToastError] = useState(null);
  const lastShownAt = useRef({});
  const dismissTimer = useRef(null);

  useEffect(() => {
    const unsub = subscribe((entry) => {
      // clearErrors() broadcasts null → also dismiss the current toast
      if (!entry) {
        setToastError(null);
        return;
      }

      // Background errors are silent
      if (entry.severity === SEVERITY.BACKGROUND) return;

      // Deduplicate by message within the window
      const now = Date.now();
      const last = lastShownAt.current[entry.message] ?? 0;
      if (now - last < DEDUP_WINDOW_MS) return;

      lastShownAt.current[entry.message] = now;

      // Clear any pending auto-dismiss before showing the new toast
      clearTimeout(dismissTimer.current);
      setToastError(entry);

      dismissTimer.current = setTimeout(() => setToastError(null), AUTO_DISMISS_MS);
    });

    return () => {
      unsub();
      clearTimeout(dismissTimer.current);
    };
  }, []);

  const dismissToast = () => {
    clearTimeout(dismissTimer.current);
    setToastError(null);
  };

  return { toastError, dismissToast };
}
